import { Container, Divider, Stack } from "@mui/material";
import objectHash from "object-hash";
import { useCallback, useState } from "react";
import DateRangeSelect from "@entrilia/shared/components/DateRangeSelect";
import PaginationControl from "@entrilia/shared/components/PaginationControl";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { api } from "../../../api/client";
import useClientContext from "../../../contexts/useClientContext";
import useClientPageTitle from "../../../contexts/useClientPageTitle";
import { pageRoutes } from "../../../routes";
import SwipeableFiltersDrawer from "../../common/reports/mobile/SwipeableFiltersDrawer";
import WidgetListToolbar from "../../widgets/mobile/WidgetListToolbar";
import NewsCategorySelect from "../NewsCategorySelect";
import {
  clearFiltersAction,
  initialNewsPageState,
  isAnyNewsFilterApplied,
  mapNewsPageStateToRequest,
  NEWS_MOBILE_PAGE_SIZE,
  newsDateRanges,
  setCategoryAction,
  setDateRangeAction,
  setPageIndexAction,
  setSearchTermAction,
} from "../newsPageState";
import NewsMobileList from "./NewsMobileList";

const getCategories = (signal?: AbortSignal) => api.investorCommunications.getCategories(signal);

const NewsMobilePage = () => {
  useClientPageTitle("News");

  const { clientCode } = useClientContext();
  const [state, setState] = useState(initialNewsPageState);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const stateHash = objectHash(state);
  const searchNews = useCallback(
    (signal?: AbortSignal) =>
      api.investorCommunications.search(mapNewsPageStateToRequest(state, NEWS_MOBILE_PAGE_SIZE), signal),
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [stateHash]
  );

  const [categories, categoriesError] = useFetch(getCategories);
  const [newsPage, fetchError, { isFetching }] = useFetch(searchNews);

  if (categoriesError) {
    logError(categoriesError, "[NewsMobilePage] getCategories");
  }

  const newsPagePath = `/${clientCode}/${pageRoutes.news}`;
  const totalPages = newsPage?.totalPages ?? 0;
  const isAnyFilterApplied = isAnyNewsFilterApplied(state);

  const selectedCategoryName = categories?.find((c) => c.externalId === state.categoryExternalId)?.name;
  const dateRangeTitle = state.dateRange?.from || state.dateRange?.to ? state.dateRange.title : undefined;
  const filterText = [selectedCategoryName, dateRangeTitle].filter(Boolean).join(" | ");

  return (
    <>
      <WidgetListToolbar
        onSearch={(searchTerm) => setState(setSearchTermAction(searchTerm))}
        onOpenFilters={() => setIsFiltersOpen(true)}
        rowCount={newsPage?.total ?? 0}
        loading={isFetching}
        isAnyFilterApplied={isAnyFilterApplied}
        filterText={filterText}
      />
      <Divider />
      <ScrollableFlexContainer>
        <Container sx={{ py: 2 }}>
          <Stack spacing={2.5}>
            <NewsMobileList
              newsPage={newsPage}
              fetchError={fetchError}
              isFetching={isFetching}
              newsPagePath={newsPagePath}
            />
            {!fetchError && !isFetching && totalPages > 1 && (
              <>
                <Divider />
                <Stack sx={{ alignItems: "center" }}>
                  <PaginationControl
                    totalPages={totalPages}
                    pageIndex={state.pageIndex}
                    onPageIndexChange={(pageIndex) => setState(setPageIndexAction(pageIndex))}
                  />
                </Stack>
              </>
            )}
          </Stack>
        </Container>
      </ScrollableFlexContainer>

      <SwipeableFiltersDrawer
        open={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        isAnyFilterApplied={isAnyFilterApplied}
        onClearFilters={() => setState(clearFiltersAction)}
        disabled={isFetching}
      >
        <NewsCategorySelect
          categories={categories ?? []}
          categoryExternalId={state.categoryExternalId}
          onChange={(categoryExternalId) => setState(setCategoryAction(categoryExternalId))}
          fullWidth
        />
        <DateRangeSelect
          predefinedRanges={newsDateRanges}
          dateRange={state.dateRange}
          onChange={(dateRange) => setState(setDateRangeAction(dateRange))}
          fullWidth
        />
      </SwipeableFiltersDrawer>
    </>
  );
};

export default NewsMobilePage;
